import { ReactNode } from 'react';

import { T } from 'react-translator-component';

import { INTEROPERABILITY_TABS, InteroperabilityTabKey } from '../tabs';

/**
 * O roteiro do tutorial de primeiro uso (MV2-1791).
 *
 * A ordem das abas já é a do ciclo de vida — elegibilidade -> convite -> contrato -> estações
 * -> transações -> histórico -> API (ver `tabs.ts`) —, mas nada na tela contava essa sequência:
 * quem abria o módulo não sabia que precisa habilitar a elegibilidade antes de convidar.
 *
 * Os passos das abas saem de `INTEROPERABILITY_TABS()`, e não de uma lista própria daqui. É o
 * que faz o passo de Elegibilidade desaparecer sozinho para quem não passa em
 * `canModerateInteroperability()`: se a aba não existe na barra, o passo não existe no roteiro,
 * e a numeração ("2 de 7") fecha sem buraco. Duas listas paralelas divergiriam na primeira aba
 * nova — e o tutorial apontaria para um alvo que não está na tela.
 *
 * `T()` é chamado aqui dentro, na montagem do roteiro, e não em constante de módulo: assim o
 * roteiro lê a permissão de moderar no momento em que o tutorial abre, e não uma vez na
 * importação do arquivo.
 */

/**
 * Os dois capítulos.
 *
 * `lifecycle` é o passeio pelas abas. `invite` é o fluxo de convite e token, que ganhou passos
 * próprios porque um balão só não dava conta: a tela tem **dois tokens diferentes** — o da
 * conta, que é endereço e pode ser reexibido, e o do convite, que é de uso único e aparece uma
 * vez — e tratar os dois como a mesma coisa é exatamente o que confunde quem chega.
 *
 * O capítulo também roda sozinho, pela entrada do cabeçalho, para quem só quer entender essa
 * parte sem repassar o módulo inteiro.
 */
export type TourChapter = 'lifecycle' | 'invite';

export type TourStep = {
  chapter: TourChapter;
  /**
   * Aba que precisa estar ativa para o alvo existir. `null` no passo de abertura e nos do
   * capítulo de convite que apontam para o cabeçalho, que vale em qualquer aba.
   */
  tab: InteroperabilityTabKey | null;
  /** Seletor CSS do alvo. Sempre um `data-tour`, para o passo não depender de classe de layout. */
  target: string;
  /**
   * Alvo que pode legitimamente não estar na tela.
   *
   * O banner de convites só existe quando há convite pendente (`PendingInvitesBanner` devolve
   * `null` sem nenhum), e o cartão do token da conta sai da tela quando a carga falha. Passo
   * marcado assim é **pulado** em vez de mostrar balão ancorado em nada.
   */
  optional?: boolean;
  title: ReactNode;
  content: ReactNode;
};

/** O atributo que marca os alvos. Fica aqui para o shell e os passos não divergirem. */
export const tourAnchor = (name: string): string => `[data-tour="${name}"]`;

/** Nome do alvo de uma aba. O shell marca o conteúdo de cada `TabPane` com isto. */
export const tabAnchorName = (key: InteroperabilityTabKey): string => `tab-${key}`;

/**
 * O fluxo de convite e parceria, passo a passo (MV2-1791).
 *
 * Um balão por ideia, na ordem em que a coisa acontece de verdade: o endereço da conta, o
 * convite que se gera com o token do parceiro, o token de uso único que se envia, o convite
 * que chega do outro lado, e por fim a lista com os dois sentidos.
 *
 * Os textos falam os mesmos nomes que os botões e modais do módulo ("Inserir token",
 * "Convidar parceiro", "Revisar convite") — tutorial que usa vocabulário próprio obriga a
 * pessoa a traduzir para achar o botão.
 */
const INVITE_FLOW_STEPS = (): TourStep[] => [
  {
    chapter: 'invite',
    tab: null,
    target: tourAnchor('token-da-conta'),
    // Sai da tela quando a carga falha.
    optional: true,
    title: T('First, your account token'),
    content: T(
      'This token is the address of your account: you hand it to a partner so they are able to invite you. It is not the invite itself — it can be shown again and renewed whenever you want, and whoever holds it can only invite you. Accepting is always your own act.',
    ),
  },
  {
    chapter: 'invite',
    tab: null,
    target: tourAnchor('acao-convidar'),
    title: T('Inviting starts with the partner token'),
    content: T(
      'Ask the partner for the token on their own Interoperability screen, paste it here and check it. The check tells you whether that account has an app configured: with no app it does not display stations, so only the one-way contract is possible. Then you pick which of your accounts enter the contract and how long the invite stays valid.',
    ),
  },
  {
    chapter: 'invite',
    tab: null,
    target: tourAnchor('acao-token'),
    title: T('The invite token is a different one'),
    content: T(
      'Generating the invite produces a second token: single use, shown only once and kept only as a hash, so it cannot be recovered. Send it to the partner through a safe channel. On the receiving side it is pasted right here, in "Enter token", where the summary says how many accounts and stations the contract shares before it is authorized.',
    ),
  },
  {
    chapter: 'invite',
    tab: null,
    target: tourAnchor('banner-convites'),
    // Só existe quando há convite pendente em algum dos dois sentidos.
    optional: true,
    title: T('A received invite waits here'),
    content: T(
      'When someone invites you, the notice shows up at the top of the screen with the time left, and "Review invite" opens the acceptance straight away — no need to hunt for the token in your e-mail.',
    ),
  },
  {
    chapter: 'invite',
    tab: 'invites',
    target: tourAnchor(tabAnchorName('invites')),
    title: T('Every invite, in both directions'),
    content: T(
      'The ones you sent and the ones you received, with what became of each: pending, used, expired or revoked. A pending invite can still be revoked; an expired one cannot be reused, and connecting that partner takes a new invite.',
    ),
  },
];

/**
 * O texto de cada aba, por chave.
 *
 * Aba sem texto aqui simplesmente não ganha passo — é o que impede uma aba nova de nascer no
 * tutorial com o balão vazio. Convites não está aqui de propósito: no lugar do passo dela entra
 * o capítulo inteiro do fluxo de convite.
 *
 * **Função, e não constante de módulo.** O `T()` resolve contra o arquivo de idioma que o
 * `Translator` carrega no construtor dele; numa constante, o `T()` rodaria na importação deste
 * arquivo — que acontece ao avaliar o bundle, **antes** de o `Translator` montar. O resultado
 * era o roteiro das abas congelado em inglês para todo tenant, enquanto os outros passos
 * (que já eram montados por função) saíam traduzidos: o mesmo balão em dois idiomas.
 * Achado pelo tourLocale.test.tsx.
 */
const TAB_STEP_TEXT = (): Partial<
  Record<InteroperabilityTabKey, { title: ReactNode; content: ReactNode }>
> => ({
  eligibility: {
    title: T('Eligibility is the gate'),
    content: T(
      'Nothing else works before this: the account and the sub-accounts you choose need to be eligible to bind. Turning it on opens data replication to the interoperability service, which is why it is behind its own permission.',
    ),
  },
  connections: {
    title: T('An accepted invite becomes a contract'),
    content: T(
      'Here are the contracts already in force, with who and in which direction. Bidirectional means both sides see each other — it depends on the partner having an app of their own. One-way means only one side sees. The same contracts also show as a map of links.',
    ),
  },
  stations: {
    title: T('What the contract delivers'),
    content: T(
      'The stations the contract made visible to you, and the ones you made visible to the partner. This is the tab that shows the result of everything above.',
    ),
  },
  transactions: {
    title: T('Charges across accounts'),
    content: T(
      'Charging sessions your drivers ran on partner stations, and the ones partner drivers ran on yours.',
    ),
  },
  audit: {
    title: T('Who changed what'),
    content: T(
      'Every change to eligibility, invites and contracts, with author and date. Use it to answer why a partner stopped seeing your stations.',
    ),
  },
  api: {
    title: T('For whoever integrates a system'),
    content: T(
      'Account settings, not day-to-day operation: the access key and the endpoint documentation, for reading this same data from outside the dashboard.',
    ),
  },
});

/**
 * O roteiro.
 *
 * Sem argumento devolve o tutorial completo, que é o do primeiro acesso. Com `chapter` devolve
 * só aquele capítulo, que é como a entrada "fluxo de convite e token" do cabeçalho o usa.
 */
export const TOUR_STEPS = (chapter?: TourChapter): TourStep[] => {
  const roteiro: TourStep[] = [
    {
      chapter: 'lifecycle',
      tab: null,
      target: tourAnchor('header'),
      title: T('What this module does'),
      content: T(
        'Interoperability lets two accounts share the visibility of their stations. The tabs follow the order of the process, left to right, and this tour walks through them in that order.',
      ),
    },
    ...INTEROPERABILITY_TABS().flatMap((tab) => {
      // O capítulo do convite entra no lugar do passo da aba Convites: é ali que ele cai na
      // ordem do ciclo de vida, e assim ele desaparece junto se a aba desaparecer.
      if (tab.key === 'invites') {
        return INVITE_FLOW_STEPS();
      }

      const text = TAB_STEP_TEXT()[tab.key];

      return text
        ? [{
          chapter: 'lifecycle' as const,
          tab: tab.key,
          target: tourAnchor(tabAnchorName(tab.key)),
          title: text.title,
          content: text.content,
        }]
        : [];
    }),
  ];

  return chapter ? roteiro.filter((step) => step.chapter === chapter) : roteiro;
};
